import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Divider, Form, Select, notification } from 'antd';
import { ModalForm } from '@ant-design/pro-form';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosApiInstance from '../../../api/axiosClient';
import ConnectionFormFields, {
    applyPrimaryFlags,
    defaultPrimaryIndices,
    PrimaryIndices,
} from '../Connections/ConnectionFormFields';

type Props = {
    editable: boolean;
    initialContacts: API.CRM.Contact[] | null | undefined;
};

type Option = {
    label: string;
    value: string;
    contact: API.CRM.Contact;
};

export const ContactLinkMigrationAlert: React.FC = () => (
    <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Linked connections"
        description="Search for an existing connection or use “Create new connection” in the dropdown below. Adding a linked connection is optional."
    />
);

/**
 * ContactLinkField — creator↔contact link control.
 *
 * - editable=true  → multi-select async search bound to the form's `contacts`
 *   field (debounced /api/connections/contacts/ lookup, chips remove on ×).
 * - editable=false → read-only antd Tag chips that navigate to the contact's
 *   view URL (/social/connections/view/:id) on click.
 */
const ContactLinkField: React.FC<Props> = ({ editable, initialContacts }) => {
    const navigate = useNavigate();

    if (!editable) {
        const contacts = initialContacts ?? [];
        if (contacts.length === 0) {
            return <span style={{ color: '#bbb', fontSize: 13 }}>No linked connections</span>;
        }
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {contacts.map((c) => {
                    const name = c.full_name?.trim() || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || '(unnamed)';
                    const initial = name.charAt(0).toUpperCase();
                    const email = c.primary_email?.email_address;
                    return (
                        <div
                            key={c.id}
                            onClick={() => navigate(`/social/connections/view/${c.id}`)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '6px 12px 6px 6px',
                                background: '#fff', border: '1px solid #d9d9d9',
                                borderRadius: 6, cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s',
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLDivElement).style.borderColor = '#1677ff';
                                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 0 0 2px rgba(22,119,255,0.1)';
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLDivElement).style.borderColor = '#d9d9d9';
                                (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                            }}
                        >
                            <div style={{
                                width: 28, height: 28, borderRadius: '50%',
                                background: '#1677ff', color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, fontWeight: 700, flexShrink: 0,
                            }}>
                                {initial}
                            </div>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>{name}</div>
                                {email && <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: 1.3 }}>{email}</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    return <EditableContactLinkField initialContacts={initialContacts} />;
};

const EditableContactLinkField: React.FC<{
    initialContacts: API.CRM.Contact[] | null | undefined;
}> = ({ initialContacts }) => {
    const form = Form.useFormInstance();
    const [search, setSearch] = useState<string>('');
    const [options, setOptions] = useState<Option[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [newConnectionModalOpen, setNewConnectionModalOpen] = useState(false);
    const [newConnectionForm] = Form.useForm<API.CRM.Contact>();
    const [primaries, setPrimaries] = useState<PrimaryIndices>(defaultPrimaryIndices);

    // Local cache so we can render labels for any contact we've ever seen,
    // including the initial ones loaded from the API and any picked from
    // search results.
    const [contactsById, setContactsById] = useState<Record<string, API.CRM.Contact>>(() => {
        const seed: Record<string, API.CRM.Contact> = {};
        for (const c of initialContacts ?? []) seed[c.id] = c;
        return seed;
    });

    useEffect(() => {
        if (!initialContacts || initialContacts.length === 0) return;
        setContactsById((prev) => {
            const next = { ...prev };
            for (const c of initialContacts) next[c.id] = c;
            return next;
        });
    }, [initialContacts]);

    // Debounced search — only query when the user has typed something.
    useEffect(() => {
        if (!search.trim()) {
            setOptions([]);
            setLoading(false);
            return;
        }

        const handle = setTimeout(() => {
            setLoading(true);
            axiosApiInstance
                .get<API.CRM.Contact[]>('/api/connections/contacts/', {
                    params: { full_name: search, current: 1, pageSize: 50 },
                })
                .then(({ data }) => {
                    const list = (Array.isArray(data) ? data : data ?? []).slice(0, 50);
                    setOptions(
                        list.map((c) => ({
                            label: formatContactLabel(c),
                            value: c.id,
                            contact: c,
                        }))
                    );
                    setContactsById((prev) => {
                        const next = { ...prev };
                        for (const c of list) next[c.id] = c;
                        return next;
                    });
                })
                .catch((err) => console.error('contact search failed', err))
                .finally(() => setLoading(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [search]);

    // Combine current options + any cached contacts that aren't in the dropdown
    // results, so the Select can render labels for already-selected items.
    const allOptions = useMemo<Option[]>(() => {
        const seen = new Set(options.map((o) => o.value));
        const extra: Option[] = [];
        for (const id of Object.keys(contactsById)) {
            if (!seen.has(id)) {
                extra.push({
                    label: formatContactLabel(contactsById[id]),
                    value: id,
                    contact: contactsById[id],
                });
            }
        }
        return [...options, ...extra];
    }, [options, contactsById]);

    const addContactToForm = (contact: API.CRM.Contact) => {
        const current = (form.getFieldValue('contacts') ?? []) as API.CRM.Contact[];
        if (current.some((c) => c.id === contact.id)) return;
        form.setFieldValue('contacts', [...current, { id: contact.id }]);
        setContactsById((prev) => ({ ...prev, [contact.id]: contact }));
        setOptions((prev) => {
            if (prev.some((o) => o.value === contact.id)) return prev;
            return [
                {
                    label: formatContactLabel(contact),
                    value: contact.id,
                    contact,
                },
                ...prev,
            ];
        });
    };

    const buildCreatedContact = (values: Partial<API.CRM.Contact>): API.CRM.Contact => {
        const primaryEmail = values.emails?.find((e) => e.is_primary) ?? values.emails?.[0];
        const primaryPhone = values.phone_numbers?.find((p) => p.is_primary) ?? values.phone_numbers?.[0];
        const primaryAddress = values.addresses?.find((a) => a.is_primary) ?? values.addresses?.[0];

        return {
            ...values,
            full_name: `${values.first_name ?? ''} ${values.last_name ?? ''}`.trim(),
            primary_email: primaryEmail,
            primary_phone_number: primaryPhone,
            primary_address: primaryAddress,
        } as API.CRM.Contact;
    };

    return (
        <>
            <Form.Item
                name="contacts"
                label="Connections"
                style={{ marginBottom: 8, width: 600 }}
                // Bidirectional transform: backend stores [{id, ...full contact}],
                // the multi-Select binds to a string[] of IDs. getValueProps unwraps
                // for display; normalize re-wraps for save.
                getValueProps={(value: API.CRM.Contact[] | undefined) => ({
                    value: (value ?? []).map((c) => c.id),
                })}
                normalize={(ids: string[]) =>
                    (ids ?? []).map((id) => ({ id })) as unknown as API.CRM.Contact[]
                }
            >
                <Select<string[]>
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="Search and add connections by name…"
                    filterOption={false}
                    loading={loading}
                    onSearch={setSearch}
                    options={allOptions}
                    notFoundContent={loading ? 'Searching…' : 'No matches'}
                    style={{ width: '100%' }}
                    dropdownRender={(menu) => (
                        <>
                            {menu}
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ padding: '0 8px 4px' }}>
                                <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    style={{ paddingLeft: 0 }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => setNewConnectionModalOpen(true)}
                                >
                                    Create new connection
                                </Button>
                            </div>
                        </>
                    )}
                />
            </Form.Item>

            <ModalForm<API.CRM.Contact>
                title="Create new connection"
                open={newConnectionModalOpen}
                width={1000}
                form={newConnectionForm}
                modalProps={{
                    destroyOnClose: true,
                    onCancel: () => {
                        setNewConnectionModalOpen(false);
                        setPrimaries(defaultPrimaryIndices);
                    },
                }}
                submitter={{
                    searchConfig: { submitText: 'Create' },
                }}
                onFinish={async (values) => {
                    const payload = applyPrimaryFlags(values, primaries);
                    try {
                        await axiosApiInstance.post('/api/connections/contacts/', payload);
                        notification.success({ message: 'Connection created' });
                        addContactToForm(buildCreatedContact(payload));
                        setPrimaries(defaultPrimaryIndices);
                        setNewConnectionModalOpen(false);
                        return true;
                    } catch (err: any) {
                        notification.error({
                            message: 'Failed to create connection',
                            description: err?.response?.data?.error ?? err?.message,
                        });
                        return false;
                    }
                }}
            >
                <ConnectionFormFields primaries={primaries} setPrimaries={setPrimaries} />
            </ModalForm>
        </>
    );
};

function formatContactLabel(c: API.CRM.Contact): string {
    const name = c.full_name?.trim() || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || '(unnamed)';
    const email = c.primary_email?.email_address;
    return email ? `${name} — ${email}` : name;
}

export default ContactLinkField;
